// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} [textInput] - The text input.
 * @property {HTMLElement} [characterCount] - The character count element.
 * @property {HTMLSelectElement} [choiceSelect] - The dropdown, for select inputs.
 * @property {HTMLElement} [notListedMessage] - Live region holding the "not listed" note, for select inputs.
 * @property {HTMLElement} [choiceValue] - Shows the chosen swatch name, for swatch inputs.
 * @property {HTMLElement} [fileName] - Shows the chosen file name, for file inputs.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  handleInput() {
    this.#updateCharacterCount();
  }

  /**
   * Reflects the current choice: the swatch name, or the "not listed" note for dropdowns.
   * @param {Event} event
   */
  handleChoice(event) {
    const { target } = event;

    if (target instanceof HTMLSelectElement) {
      const selected = target.selectedOptions[0];
      const isNotListed = Boolean(selected?.hasAttribute('data-not-listed'));
      const note = this.refs.notListedMessage?.querySelector('p');
      if (note) note.hidden = !isNotListed;

      // `required` only rejects the first empty option, so block "not listed" explicitly
      target.setCustomValidity(isNotListed ? note?.textContent?.trim() || selected?.textContent?.trim() || '' : '');
      return;
    }

    if (target instanceof HTMLInputElement && this.refs.choiceValue) {
      this.refs.choiceValue.textContent = target.value;
    }
  }

  /**
   * Shows the chosen file's name in place of the upload prompt.
   * @param {Event} event
   */
  handleFile(event) {
    const { target } = event;
    const { fileName } = this.refs;
    if (!(target instanceof HTMLInputElement) || !fileName) return;

    fileName.textContent = target.files?.[0]?.name || fileName.dataset.empty || '';
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;

    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
